import {
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useToast } from "hooks";

import { formMessages } from "messages";
import {
  useDeleteChartFavorite,
  useUpdateChartFavoriteInfo,
} from "modules/charts-shared/api";
import { ChartFavorite } from "modules/charts-shared/types";
import { mapPayloadToFormObject } from "modules/charts-shared/utils";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  EmrHeart,
  SiEdit,
  EmrHeartOutline,
  EmrRemove,
} from "@medstonetech/slate-icons";
import { Button, Card, WarningDialog } from "shared";
import { extractApiErrorMessage } from "utils";
import { ChartFavoriteRenameModal } from "./ChartFavoriteNameModal";

type TreatmentItemProps = {
  item: ChartFavorite;
  onDelete: () => void;
  isDeleteLoading: boolean;
  isEdit: boolean;
  chartFavoriteIdSelected?: string | null;
  onCloseModal: () => void;
  chartCode: string;
};

function ChartFavoriteItem({
  item,
  onDelete,
  isDeleteLoading,
  isEdit,
  onCloseModal,
  chartCode,
}: TreatmentItemProps) {
  const toast = useToast();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { reset, setValue } = useFormContext();

  const {
    mutateAsync: updateChartFavorite,
    isLoading: isUpdateChartFavoriteLoading,
  } = useUpdateChartFavoriteInfo({
    id: item.id,
    sectionCode: item.sectionCode,
  });

  const handleToggleFavorite = async () => {
    try {
      const payload = {
        title: item.title,
        isFavorite: !item.isFavorite,
      };

      await updateChartFavorite(payload);

      toast({
        status: "success",
        description: formMessages.updateSuccess("Chart Favorite"),
      });
    } catch (err) {
      toast({ status: "error", description: extractApiErrorMessage(err) });
    }
  };

  const handleSelectFavorite = async () => {
    try {
      const form = mapPayloadToFormObject(
        item.subSections.map((x) => ({
          ...x,
          id: "",
          answers: x.answers.map((answer) => ({
            qCode: `${chartCode}${answer.qCode}`,
            value: answer.value,
          })),
        }))
      );
      reset(form);
      setValue("chartFavoriteId", item.id, { shouldDirty: true });
      onCloseModal();
    } catch (err) {
      toast({ status: "error", description: extractApiErrorMessage(err) });
    }
  };

  return (
    <Flex height="52px" p="15px" alignItems="center" columnGap="16px">
      <IconButton
        variant="icon"
        aria-label="Favorite"
        onClick={handleToggleFavorite}
        isLoading={isUpdateChartFavoriteLoading}
        isDisabled={isUpdateChartFavoriteLoading}
      >
        <Icon
          as={item.isFavorite ? EmrHeart : EmrHeartOutline}
          color={item.isFavorite ? "blue" : "gray"}
        />
      </IconButton>
      <Text flex={1} sx={{ cursor: "pointer" }} onClick={handleSelectFavorite}>
        {item.title}
      </Text>
      <Box display="flex" gap={2} alignItems="center">
        {isEdit && (
          <>
            <IconButton variant="icon" aria-label="Delete" onClick={onOpen}>
              <Icon as={SiEdit} color="blue" />
            </IconButton>
            <IconButton
              variant="icon"
              aria-label="Delete"
              onClick={onDelete}
              isLoading={isDeleteLoading}
            >
              <Icon as={EmrRemove} color="red" />
            </IconButton>
          </>
        )}
      </Box>
      {isOpen && (
        <ChartFavoriteRenameModal
          item={item}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
    </Flex>
  );
}

type ChartFavoritesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  chartFavorites: ChartFavorite[];
  sectionCode: string;
  chartFavoriteIdSelected?: string | null;
  chartCode: string;
};

function ChartFavoritesModal({
  isOpen,
  onClose,
  chartFavorites,
  sectionCode,
  chartFavoriteIdSelected,
  chartCode,
}: ChartFavoritesModalProps) {
  const toast = useToast();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState(false);

  const {
    mutateAsync: deleteChartFavorite,
    isLoading: isDeleteFavoriteLoading,
  } = useDeleteChartFavorite({ id: deletingId ?? "", sectionCode });

  const handleDeleteElement = async () => {
    try {
      if (deletingId) {
        await deleteChartFavorite({});
        toast({
          status: "success",
          description: formMessages.deleteSuccess("Chart Favorite"),
        });
        setDeletingId(null);
      }
    } catch (err) {
      toast({ status: "error", description: extractApiErrorMessage(err) });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        bg="gray.200"
        maxW="unset"
        width="540px"
        containerProps={{ justifyContent: "flex-end" }}
        margin="auto"
        minHeight="350px"
        overflowY="auto"
        overflowX="hidden"
      >
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          fontSize="1rem"
          padding="20px 16px 30px"
          alignItems="center"
        >
          <Box flex={1} />
          <Heading
            fontWeight="600"
            fontSize="1.0625rem"
            textAlign="center"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <Icon as={EmrHeartOutline} fontSize="24px" />
            Favorites
          </Heading>
          <Flex flex={1} justifyContent="flex-end">
            <Button variant="label" onClick={onClose} color="blue">
              Close
            </Button>
          </Flex>
        </ModalHeader>
        <ModalBody p="15px">
          <Box
            width="100%"
            display="flex"
            justifyContent="end"
            pt="20px"
            pr={2}
          >
            <Button
              variant="label"
              onClick={() => setIsEdit((prev) => !prev)}
              color="blue"
            >
              {isEdit ? "Done" : "Edit List"}
            </Button>
          </Box>

          <Card overflowY="auto" overflowX="hidden" mt="20px">
            {chartFavorites.map((item, i) => (
              <React.Fragment key={item.id}>
                <ChartFavoriteItem
                  item={item}
                  onDelete={() => setDeletingId(item.id)}
                  isDeleteLoading={
                    item.id === deletingId || isDeleteFavoriteLoading
                  }
                  isEdit={isEdit}
                  chartFavoriteIdSelected={chartFavoriteIdSelected}
                  onCloseModal={onClose}
                  chartCode={chartCode}
                />
                {i < chartFavorites.length - 1 && <Divider marginLeft="52px" />}
              </React.Fragment>
            ))}
          </Card>
        </ModalBody>
      </ModalContent>
      <WarningDialog
        isOpen={Boolean(deletingId)}
        onCancel={() => setDeletingId(null)}
        onAction={handleDeleteElement}
        onClose={() => setDeletingId(null)}
        title="Warning"
        mainText={`Are you sure you want to Delete this Favorite\rThis action cannot be undone!`}
      />
    </Modal>
  );
}

export { ChartFavoritesModal };
